import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { confirmDialog } from 'primereact/confirmdialog';
import { useEsMovil } from '../../indicadores/hooks/useEsMovil';
import { MetasApiError, MetasColaboradorService } from '../services/metasColaborador.service';
import type { MiAlcance, ResultadoLote, SemanaTiendas, TiendaRef, TiendaSemana, VistaTienda } from '../types/metas.types';
import {
  coincideBusqueda,
  coincideFiltro,
  conteosPorFiltro,
  eligeSupervisor,
  etiquetaFiltro,
  FiltroAutorizacion,
  filtroPorOmision,
  filtrosAutorizacion,
  kpisAvance,
  llaveFila,
  metasExcepcionales,
  nombreTienda,
  paginaDe,
  paginar,
  quienEnvioYResolvio,
  regionalesDe,
  resumenLote,
  subtituloTienda,
  supervisorDe,
  supervisoresDe,
  TAMANO_PAGINA,
  textoEstadoLista,
  textoTiendas,
  tonoDiferencia,
} from '../utils/autorizacion';
import { guardarArchivo } from '../utils/descarga';
import { veTodasLasTiendas } from '../utils/direccion';
import { capturaDeTienda } from '../utils/flujo';
import { nombrePropio, pctConSigno, pesosEnteros } from '../utils/formato';
import { textoMetaIncompleta } from '../utils/kpis';
import { numeroSemana } from '../utils/semanas';
import { GRUPO_CONFIRMAR } from '../utils/confirmar';
import { CapturaLecturaDialog } from './CapturaLecturaDialog';
import { PanelDetalleTienda } from './PanelDetalleTienda';
import { ID_SECCION_PROPUESTAS, PropuestasCatalogoSeccion } from './PropuestasCatalogoSeccion';
import { Chip } from './ui/Chip';
import { CLASE_BOTON, CLASE_BOTON_ICONO, CLASE_BOTON_PRIMARIO, CLASE_OVERLAY, CLASE_PANEL, CLASES_CAMPO, cx } from './ui/clases';
import { Field } from './ui/Field';
import { EstadoPill, Pill } from './ui/Pill';

type AvisoToast = (severidad: 'success' | 'warn' | 'error', resumen: string, detalle?: string) => void;

interface Props {
  alcance: MiAlcance;
  lunes: string;
  onAviso: AvisoToast;
  /**
   * Cambia cuando se propuso, retiró o resolvió algo del catálogo desde la
   * Captura: la lista (su «N de catálogo por resolver») y las propuestas se recargan.
   */
  recarga?: number;
  /**
   * La pestaña está a la vista. Mientras no se abra por primera vez no se pide
   * nada (una cuenta mixta que sólo usa la Captura no arma detalles con BUK y el
   * ERP); al volver a ella se releen la lista, las propuestas y el contador.
   */
  activa?: boolean;
  /** La tienda del enlace (`?tienda=VAZ-1`): se abre con la primera lista, UNA vez. */
  tiendaInicial?: TiendaRef | null;
  /** Cada vez que cambia la tienda abierta (o `null`): la pantalla la escribe en el enlace. */
  onTienda?: (t: TiendaRef | null) => void;
  /** Se autorizó, rechazó o reabrió algo: la pantalla relee los contadores de las pestañas. */
  onCambio?: () => void;
  /** Enlace a la Captura de una tienda; `null` si la cuenta no tiene esa pestaña (se abre la de sólo lectura). */
  hrefCaptura?: (t: TiendaRef) => string | null;
  /** «Ver captura completa» cuando hay enlace: la pantalla cambia de pestaña y abre la tienda. */
  onIrACaptura?: (t: TiendaRef) => void;
  /**
   * Abrir ESTA tienda en el detalle («Ver tienda» desde Pendientes de catálogo,
   * para quien no captura). `n` cambia en cada pedido: la misma tienda dos veces
   * también cuenta.
   */
  abrirTienda?: (TiendaRef & { n: number }) | null;
  /**
   * «Seguimiento» de dirección (21-oct-2026): la misma lista y el mismo detalle,
   * SIN decisiones —ni casillas, ni «Autorizar seleccionadas», ni autorizar,
   * rechazar o reabrir— y con quién envió y quién resolvió cada tienda. La API
   * ya manda todos los permisos en falso; esto quita también los controles.
   */
  soloLectura?: boolean;
}

const IconoTriangulo = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l10 18H2z" />
    <path d="M12 10v4M12 17.5h.01" />
  </svg>
);

const Flecha: React.FC<{ dir: 'izq' | 'der' | 'atras' }> = ({ dir }) => (
  <svg width={dir === 'atras' ? 22 : 18} height={dir === 'atras' ? 22 : 18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={dir === 'izq' ? 'M15 6l-6 6 6 6' : dir === 'der' ? 'M9 6l6 6-6 6' : 'M19 12H5M11 6l-6 6 6 6'} />
  </svg>
);

const CLASE_DIF = { baja: 'font-semibold text-metas-error', alta: 'font-semibold text-metas-exito', neutra: 'text-metas-texto-2' } as const;

/**
 * El valor elegido de los filtros de regional y supervisor (revisión de la
 * fase B, 21-oct-2026): la etiqueta del Dropdown es flex y su texto suelto se
 * cortaba a media palabra sin «…» («Todos los regionales · 158 t»). Envuelto
 * en `truncate`, si no cabe lleva «…», y el texto completo va en `title`.
 */
const valorFiltro = (o: { etiqueta: string } | null) => {
  const texto = o?.etiqueta ?? '';
  return (
    <span className="block min-w-0 truncate" title={texto}>
      {texto}
    </span>
  );
};

/** Tiene pendientes de catálogo que bloquean su autorización (fase 4). */
export const bloqueadaPorCatalogo = (t: TiendaSemana): boolean => (t.pendientesBloqueantes ?? 0) > 0;

/** Por qué la casilla del lote está deshabilitada en una enviada. */
export const porQueFueraDeLote = (t: TiendaSemana): string =>
  bloqueadaPorCatalogo(t)
    ? t.fueraDeLote ||
      `Tiene ${t.pendientesBloqueantes} pendiente(s) de catálogo que bloquean la autorización: revísalos en el detalle.`
    : t.fueraDeLote && t.alertas === 0
      ? t.fueraDeLote
      : 'Tiene alertas (o no se ha revisado): autorízala desde el detalle';

const IconoCandado = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

/** «Bloqueada por catálogo»: candado + texto (el color nunca va solo). */
const MarcaBloqueadaCatalogo: React.FC<{ t: TiendaSemana }> = ({ t }) =>
  bloqueadaPorCatalogo(t) ? (
    <span className="mt-1 self-start" data-testid="bloqueada-catalogo" title={porQueFueraDeLote(t)}>
      <Pill tono="error" icono={<IconoCandado />}>
        Bloqueada por catálogo
      </Pill>
    </span>
  ) : null;

/**
 * «Meta a bodeguero» y «Meta a Casa» junto al nombre de la tienda (ronda F,
 * 23-oct-2026, decisión de la sesión, revertible): sin esto, una tienda con meta
 * al bodeguero y sin otra alerta se autorizaba en lote sin que el regional lo
 * viera (sólo lo decía el detalle). Sale de la revisión guardada de la fila; no
 * la saca del lote. Con el nombre y la meta en el `title` y para el lector de pantalla.
 */
export const InsigniasMetaExcepcional: React.FC<{ t: TiendaSemana }> = ({ t }) => {
  const metas = metasExcepcionales(t.revision);
  if (!metas.length) return null;
  return (
    <span className="mt-1 flex flex-wrap gap-1 self-start" data-testid="metas-excepcionales">
      {metas.map((m) => (
        <Pill key={m.tipo} tono="aviso" icono={<IconoTriangulo />} title={m.detalle}>
          {m.etiqueta}
          <span className="sr-only">: {m.detalle}</span>
        </Pill>
      ))}
    </span>
  );
};

/** La celda «Alertas»: pastilla ámbar con conteo, «—» sin alertas, «Por revisar» si no se sabe. */
const CeldaAlertas: React.FC<{ t: TiendaSemana }> = ({ t }) => {
  if (!t.resumen) return <span className="text-metas-texto-2">—</span>;
  if (t.alertas === null || t.alertas === undefined)
    return (
      <span className="text-xs text-metas-texto-2" title="El resumen es de antes de la revisión automática: se revisa al abrir el detalle">
        Por revisar
      </span>
    );
  if (t.alertas > 0)
    return (
      <Pill tono="aviso" icono={<IconoTriangulo />} className="!px-2" title={`${t.alertas} alerta(s): autorízala desde el detalle`}>
        <span className="num">{t.alertas}</span>
        <span className="sr-only"> {t.alertas === 1 ? 'alerta' : 'alertas'}</span>
      </Pill>
    );
  // Sin alertas pero sin poder comparar la suma (meta de tienda incompleta con la
  // fuente ya cargada): no es «sin alertas» y no va en el lote. Si lo que la saca
  // del lote es un pendiente de catálogo, eso lo dice su marca junto al nombre.
  if (t.fueraDeLote && !bloqueadaPorCatalogo(t))
    return (
      <span className="text-xs text-metas-texto-2" title={t.fueraDeLote}>
        Suma sin comparar
      </span>
    );
  return (
    <span className="text-metas-texto-2">
      —<span className="sr-only">sin alertas</span>
    </span>
  );
};

/**
 * Autorización del regional (fase 3 del rediseño, artboards `Autorizacion` y
 * `MovilAutorizacion`): la lista de tiendas de la semana a la izquierda y el
 * detalle de la elegida a la derecha; en teléfono, tienda por tienda.
 *
 * LOTE: «Autorizar seleccionadas» sólo lleva tiendas ENVIADAS y SIN alertas
 * (`puedeAutorizarEnLote`). La API vuelve a revisar cada una dentro de su
 * transacción: si al recalcular aparece una alerta, la OMITE y lo dice; lo que
 * falle por otra cosa va en `fallidas`. Las que tienen alertas se autorizan una
 * por una desde el detalle.
 *
 * FILTROS: regional (administrador y dirección), supervisor (21-oct-2026,
 * pedido para el visor del regional: también el regional; depende del regional
 * elegido), búsqueda y chips. Todos en el navegador, con lo que ya trae la
 * lista (`supervisor` y `regional` de cada tienda): la API no cambia. Cambiar
 * cualquiera suelta la página y el lote marcado.
 *
 * CON QUIÉN ESTÁ (21-oct-2026, fase B, diseños `Regional-filtro` y
 * `Direccion-seguimiento`): el gerente captura y se la manda al supervisor
 * (`revision`), que la envía al regional. Los chips y la pastilla de cada
 * tienda lo dicen: «Por autorizar», «Con el supervisor», «Con el gerente»…;
 * en «Seguimiento», además, el avance de la semana en cuatro números.
 * Sólo con la captura de la tienda encendida (`capturaTienda`, por entorno).
 * Por omisión está APAGADA (22-oct-2026, decisión del usuario: «de momento
 * seguir dejando la tarea de capturar la meta a los supervisores»): los chips
 * de antes («En captura» junta sin captura y borrador) y, en el avance,
 * «Rechazadas» y «En captura» en lugar del supervisor y el gerente.
 *
 * SEGUIMIENTO (`soloLectura`, 21-oct-2026): dirección ve esta misma pestaña,
 * con el nombre «Seguimiento», sobre TODAS las tiendas y con el filtro de
 * regional del administrador, pero sin nada que decida. Ninguna tienda se abre
 * sola: cada detalle arma la plantilla con BUK y el ERP, y aquí no hay una
 * «primera que espera tu decisión».
 *
 * ⚠️ La selección guarda la VERSIÓN de cada tienda del momento en que se marcó
 * y se limpia en cada recarga; al autorizar, la versión sale de la fila
 * VIGENTE en `datos` (con la copia vieja, una tienda que el supervisor reenvió
 * volvía con 409 aunque la lista ya mostrara lo nuevo). La excepción es la
 * recarga por el catálogo (`recarga`): la semana es la misma y se conservan las
 * que siguen autorizables en lote con la misma versión.
 */
export const AutorizacionTab: React.FC<Props> = ({
  alcance,
  lunes,
  onAviso,
  recarga,
  activa = true,
  onCambio,
  hrefCaptura,
  onIrACaptura,
  tiendaInicial,
  onTienda,
  abrirTienda,
  soloLectura = false,
}) => {
  // Hasta 1023 px la disposición del teléfono (auditoría del 22-oct-2026): a 768 la
  // tabla de 640 px cortaba el nombre en «VAZ 1…» y las alertas, y el detalle
  // quedaba debajo sin que nada llevara a él. De 1024 a 1199, la lista y el
  // detalle apilados: al elegir una tienda se desplaza hasta el detalle.
  const esMovil = useEsMovil(1023);
  const apilada = useEsMovil(1199);
  // ¿Captura la tienda (el gerente)? Lo dice el entorno (22-oct-2026); por omisión, no.
  const capturaTienda = capturaDeTienda(alcance);
  // El filtro de regional: el administrador y dirección, que ven las dos regionales.
  const eligeRegional = veTodasLasTiendas(alcance);
  // El de supervisor (21-oct-2026): además el regional, que ve los de su regional.
  const filtraSupervisor = eligeSupervisor(alcance);
  // Se abrió alguna vez: desde entonces la lista sigue a la semana aunque la pestaña se oculte.
  const [abierta, setAbierta] = useState(activa);
  useEffect(() => {
    if (activa) setAbierta(true);
  }, [activa]);
  const abiertaRef = useRef(abierta);
  abiertaRef.current = abierta;
  const tiendaPedida = useRef<TiendaRef | null>(tiendaInicial ?? null);
  const onTiendaRef = useRef(onTienda);
  onTiendaRef.current = onTienda;
  const [datos, setDatos] = useState<SemanaTiendas | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtroElegido, setFiltroElegido] = useState<FiltroAutorizacion | null>(null);
  const [regional, setRegional] = useState<string>('');
  const [supervisor, setSupervisor] = useState<string>('');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(0);
  const [seleccion, setSeleccion] = useState<Map<string, number>>(() => new Map());
  const [elegida, setElegida] = useState<string | null>(null);
  const [detalleMovil, setDetalleMovil] = useState(false);
  const [resultado, setResultado] = useState<ResultadoLote | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [capturaLectura, setCapturaLectura] = useState<VistaTienda | null>(null);
  const [filtroPropuestas, setFiltroPropuestas] = useState<TiendaRef | null>(null);
  const [recargaPropuestas, setRecargaPropuestas] = useState(0);
  // Un pedido de abrir una tienda: se suelta la elegida y la de abajo la toma
  // en cuanto la lista (de esta semana) la tenga.
  useEffect(() => {
    if (!abrirTienda) return;
    tiendaPedida.current = { chain: abrirTienda.chain, num: abrirTienda.num };
    setElegida(null);
  }, [abrirTienda]);
  const onCambioRef = useRef(onCambio);
  onCambioRef.current = onCambio;

  // Sólo cuenta la respuesta del ÚLTIMO pedido: al cambiar rápido de semana,
  // la lista de la semana anterior que llegaba tarde se quedaba en pantalla.
  const pedido = useRef(0);
  const datosRef = useRef<SemanaTiendas | null>(null);
  // La semana que está en pantalla AHORA: un lote que termina después de
  // cambiar de semana no recarga la anterior.
  const lunesVigente = useRef(lunes);
  lunesVigente.current = lunes;

  const cargar = useCallback(
    async (opciones?: { conservarSeleccion?: boolean }) => {
      const conservar = !!opciones?.conservarSeleccion;
      const n = ++pedido.current;
      setCargando(true);
      setError(null);
      if (!conservar) setSeleccion(new Map());
      try {
        const d = await MetasColaboradorService.listarTiendas(lunes);
        if (n !== pedido.current) return;
        datosRef.current = d;
        setDatos(d);
        if (conservar) {
          // Recarga por el catálogo: la semana no cambió. Se quedan las que
          // siguen autorizables en lote con la MISMA versión (una que el
          // supervisor reenvió es otra captura: se vuelve a elegir).
          setSeleccion((s) => {
            const m = new Map<string, number>();
            s.forEach((version, k) => {
              const t = d.tiendas.find((y) => llaveFila(y) === k);
              if (t && t.puedeAutorizarEnLote && t.version === version) m.set(k, version);
            });
            return m;
          });
        } else {
          setSeleccion(new Map());
        }
      } catch (e) {
        if (n !== pedido.current) return;
        setError((e as Error).message);
      } finally {
        if (n === pedido.current) setCargando(false);
      }
    },
    [lunes],
  );

  // Semana nueva: todo lo elegido era de la anterior.
  useEffect(() => {
    setResultado(null);
    setFiltroElegido(null);
    setElegida(null);
    setDetalleMovil(false);
    setPagina(0);
    setDatos(null);
    datosRef.current = null;
    if (abierta) void cargar();
  }, [cargar, abierta]);

  // Algo cambió desde la Captura (catálogo, un envío) o se decidió algo aquí.
  // Por referencia, para no recargar dos veces al cambiar de semana. Conserva el lote marcado.
  const cargarVigente = useRef(cargar);
  cargarVigente.current = cargar;
  useEffect(() => {
    if (recarga) {
      if (abiertaRef.current) void cargarVigente.current({ conservarSeleccion: true });
      setRecargaPropuestas((n) => n + 1);
    }
  }, [recarga]);

  // Al VOLVER a la pestaña: mientras se veía otra, un supervisor pudo enviar o
  // proponer. Las propuestas ya se releían; la lista y el contador no, y las dos
  // partes de la pantalla se contradecían.
  const activaAntes = useRef(activa);
  useEffect(() => {
    const era = activaAntes.current;
    activaAntes.current = activa;
    if (activa && !era && datosRef.current) {
      void cargarVigente.current({ conservarSeleccion: true });
      onCambioRef.current?.();
    }
  }, [activa]);

  // ------------------------------------------------------------ filtros
  const tiendas = useMemo(() => datos?.tiendas ?? [], [datos]);
  const regionales = useMemo(() => regionalesDe(tiendas), [tiendas]);
  const delRegional = useMemo(
    () => tiendas.filter((t) => !eligeRegional || !regional || (t.regional || '(sin regional)') === regional),
    [tiendas, eligeRegional, regional],
  );
  // Supervisor (21-oct-2026): en el navegador, con lo que ya trae la lista, y
  // sólo entre los del regional elegido. Si el elegido ya no sale (otra semana
  // sin sus tiendas), no filtra: una lista vacía sin decir por qué confunde.
  const supervisores = useMemo(() => supervisoresDe(delRegional), [delRegional]);
  const supervisorVigente = filtraSupervisor && supervisores.some((s) => s.nombre === supervisor) ? supervisor : '';
  const base = useMemo(
    () =>
      delRegional.filter(
        (t) => (!supervisorVigente || supervisorDe(t) === supervisorVigente) && coincideBusqueda(t, busqueda),
      ),
    [delRegional, supervisorVigente, busqueda],
  );
  const conteos = useMemo(() => conteosPorFiltro(base), [base]);
  const filtro: FiltroAutorizacion = filtroElegido ?? filtroPorOmision(conteos);
  const visibles = useMemo(() => base.filter((t) => coincideFiltro(t.estado, filtro)), [base, filtro]);
  const pag = paginar(visibles, pagina, TAMANO_PAGINA);

  const filaDe = useCallback((k: string | null) => (k ? tiendas.find((t) => llaveFila(t) === k) ?? null : null), [tiendas]);
  const filaElegida = filaDe(elegida);

  // La tienda del enlace, con la primera lista y UNA vez. Si no, en escritorio
  // se abre sola la primera que espera decisión de esta cuenta, sólo con la
  // pestaña a la vista (una sola: cada detalle arma la plantilla con BUK y el
  // ERP). Si la elegida ya no existe (otra semana), se suelta.
  useEffect(() => {
    if (!datos) return;
    if (elegida && !filaDe(elegida)) {
      setElegida(null);
      setDetalleMovil(false);
      return;
    }
    if (elegida) return;
    const pedida = tiendaPedida.current;
    if (pedida) {
      tiendaPedida.current = null;
      const f = tiendas.find((t) => t.chain === pedida.chain && t.num === pedida.num);
      if (f) {
        if (!visibles.includes(f)) setFiltroElegido('todas');
        setElegida(llaveFila(f));
        if (esMovil) setDetalleMovil(true);
        return;
      }
    }
    if (!esMovil && activa && !soloLectura) {
      const primera = visibles.find((t) => t.puedeAutorizar);
      if (primera) setElegida(llaveFila(primera));
    }
  }, [datos, elegida, esMovil, visibles, filaDe, tiendas, activa, soloLectura]);

  // La lista va a la página de la tienda elegida (revisión de la fase B,
  // 21-oct-2026): abierta desde el enlace, «Ver tienda» o «Ver detalle» del
  // lote, el detalle se veía con la lista en la página 1 y sin la tienda
  // marcada. Una vez por tienda elegida: si luego se pagina a mano, la lista se
  // queda donde la dejó la persona.
  const elegidaLlevada = useRef<string | null>(null);
  useEffect(() => {
    if (!elegida) {
      elegidaLlevada.current = null;
      return;
    }
    if (elegidaLlevada.current === elegida) return;
    const p = paginaDe(visibles, elegida, llaveFila);
    // Todavía no está en la lista a la vista (la lista o el chip «Todas» vienen en camino).
    if (p === null) return;
    elegidaLlevada.current = elegida;
    if (p !== pagina) setPagina(p);
  }, [elegida, visibles, pagina]);

  // La tienda abierta va al enlace (`?tienda=`), para compartirlo.
  useEffect(() => {
    if (!datos) return;
    const f = elegida ? filaDe(elegida) : null;
    onTiendaRef.current?.(f ? { chain: f.chain, num: f.num } : null);
  }, [datos, elegida, filaDe]);

  // Cambiar lo que se ve (búsqueda, regional, chip) suelta el lote: si no,
  // «Autorizar seleccionadas» llevaba tiendas que ya no estaban en pantalla.
  const cambiarFiltro = (f: FiltroAutorizacion) => {
    setFiltroElegido(f);
    setPagina(0);
    setSeleccion(new Map());
  };

  // ------------------------------------------------------------ selección y lote
  // Sólo lo que está a la vista: la casilla de la cabecera marca las de ESTA
  // página, y el lote sólo lleva las marcadas que siguen en la lista filtrada.
  const seleccionables = pag.filas.filter((t) => t.puedeAutorizarEnLote);
  const marcadas = visibles.filter((t) => t.puedeAutorizarEnLote && seleccion.has(llaveFila(t))).map(llaveFila);
  const todasMarcadas = seleccionables.length > 0 && seleccionables.every((t) => seleccion.has(llaveFila(t)));
  const algunaMarcada = seleccionables.some((t) => seleccion.has(llaveFila(t)));
  const casillaTodas = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (casillaTodas.current) casillaTodas.current.indeterminate = algunaMarcada && !todasMarcadas;
  });

  const marcar = (t: TiendaSemana, si: boolean) =>
    setSeleccion((s) => {
      const m = new Map(s);
      if (si && t.puedeAutorizarEnLote) m.set(llaveFila(t), t.version);
      else m.delete(llaveFila(t));
      return m;
    });

  const marcarTodas = (si: boolean) =>
    setSeleccion((s) => {
      const m = new Map(s);
      for (const t of seleccionables) {
        if (si) m.set(llaveFila(t), t.version);
        else m.delete(llaveFila(t));
      }
      return m;
    });

  const trasResolver = useCallback(() => {
    if (lunesVigente.current === lunes) void cargar();
    onCambioRef.current?.();
  }, [cargar, lunes]);

  const autorizarSeleccion = () => {
    if (!marcadas.length) return;
    const claves = [...marcadas];
    const semana = lunes;
    // Con nombre: el número solo no dice QUÉ se va a autorizar.
    const nombres = claves.map((k) => {
      const t = filaDe(k);
      return t ? nombreTienda(t) : k.replace('|', ' ');
    });
    const listado = nombres.length > 10 ? `${nombres.slice(0, 10).join(', ')} y ${nombres.length - 10} más` : nombres.join(', ');
    // Ronda F (decisión de la sesión): las que llevan meta a bodeguero o a la Casa se dicen aquí también.
    const conExcepcion = claves
      .map((k) => filaDe(k))
      .filter((t): t is TiendaSemana => !!t && metasExcepcionales(t.revision).length > 0);
    const ojo = conExcepcion.length
      ? ` Ojo: ${conExcepcion.length === 1 ? '1 de ellas lleva' : `${conExcepcion.length} de ellas llevan`} meta a bodeguero o a la Casa (${conExcepcion
          .map((t) => `${nombreTienda(t)}: ${metasExcepcionales(t.revision).map((m) => m.etiqueta.charAt(0).toLowerCase() + m.etiqueta.slice(1)).join(' y ')}`)
          .join('; ')}).`
      : '';
    confirmDialog({
      group: GRUPO_CONFIRMAR,
      header: 'Autorizar tiendas',
      message: `Se autorizan ${claves.length} tienda(s) completas (${listado}), tal como las enviaron sus supervisores. Si al revisarlas otra vez alguna tiene alertas o un pendiente de catálogo que la bloquea, se omite y lo dice.${ojo}`,
      icon: 'pi pi-check-circle',
      acceptLabel: 'Sí, autorizar',
      rejectLabel: 'Cancelar',
      accept: async () => {
        // Cada tienda con la versión de la fila VIGENTE, no la de cuando se
        // marcó, y sólo si sigue siendo autorizable en lote.
        const d = datosRef.current;
        if (!d || d.semana.lunes !== semana) {
          onAviso('warn', 'No se autorizó nada', 'Cambió la semana en pantalla: vuelve a seleccionar las tiendas.');
          return;
        }
        const vigentes = claves
          .map((k) => d.tiendas.find((t) => llaveFila(t) === k))
          .filter((t): t is TiendaSemana => !!t && !!t.puedeAutorizarEnLote);
        if (!vigentes.length) {
          onAviso('warn', 'No se autorizó nada', 'Las tiendas seleccionadas ya no están por autorizar sin alertas.');
          return;
        }
        setTrabajando(true);
        try {
          const r = await MetasColaboradorService.autorizarLote(
            semana,
            vigentes.map((t) => ({ chain: t.chain, num: t.num, version: t.version })),
          );
          setResultado(r);
          const conProblemas = r.omitidas.length + r.fallidas.length > 0;
          onAviso(
            conProblemas ? 'warn' : 'success',
            `${r.autorizadas.length} autorizada(s)`,
            conProblemas ? `${resumenLote(r)}: revisa el resultado.` : undefined,
          );
          setSeleccion(new Map());
          if (lunesVigente.current === semana) await cargar();
          onCambioRef.current?.();
        } catch (e) {
          onAviso('error', 'No se pudo autorizar', (e as Error).message);
        } finally {
          setTrabajando(false);
        }
      },
    });
  };

  // Sólo administradores (la API responde 403 a los demás). Es el puente al
  // cálculo de comisiones: el archivo se pasa a `comisiones-spec-semana.cjs`.
  const bajarAutorizadas = async () => {
    setDescargando(true);
    try {
      guardarArchivo(await MetasColaboradorService.autorizadasExcel(lunes));
    } catch (e) {
      // 409 = la semana aún se puede autorizar o rechazar: no es una falla.
      const conflicto = e instanceof MetasApiError && e.status === 409;
      onAviso(conflicto ? 'warn' : 'error', 'No se pudieron bajar las autorizadas', (e as Error).message);
    } finally {
      setDescargando(false);
    }
  };

  // ------------------------------------------------------------ detalle
  const abrir = (t: TiendaRef) => {
    setElegida(llaveFila(t));
    if (esMovil) setDetalleMovil(true);
    else if (apilada) setPedirDetalle((n) => n + 1);
  };

  // De 1024 a 1199 px el detalle queda bajo la lista (con el título a ~1.100 px de
  // la fila tocada): al elegir una tienda, la página baja hasta él.
  const refDetalle = useRef<HTMLDivElement>(null);
  const [pedirDetalle, setPedirDetalle] = useState(0);
  useEffect(() => {
    if (!pedirDetalle) return;
    const el = refDetalle.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [pedirDetalle]);

  // Teléfono: al volver a la lista, el foco regresa a la tarjeta de la tienda
  // que estaba abierta (si no, caía en <body> y se perdía el lugar en la lista).
  const volverA = useRef<string | null>(null);
  const volverALista = () => {
    volverA.current = elegida;
    setDetalleMovil(false);
  };
  useEffect(() => {
    if (detalleMovil || !volverA.current) return;
    const k = volverA.current;
    volverA.current = null;
    const tarjeta = Array.from(document.querySelectorAll<HTMLElement>('[data-tienda-movil]')).find(
      (el) => el.getAttribute('data-tienda-movil') === k,
    );
    tarjeta?.focus();
  }, [detalleMovil]);

  /**
   * El panel avisa cada decisión que el servidor tomó, aunque ya se vea otra
   * tienda. En el teléfono, si la resuelta es la que sigue abierta, se vuelve a
   * la lista con el foco en su tarjeta (auditoría del 22-oct-2026): antes se abría
   * sola la siguiente, con «Autorizar» justo bajo el dedo —al rechazar VAZ 189 se
   * abrió TRE 170, una tienda real—, y un doble toque la autorizaba sin verla.
   */
  const alResolver = (mensaje: string, t: TiendaRef) => {
    onAviso('success', 'Listo', mensaje);
    if (esMovil && elegida && elegida === llaveFila(t)) volverALista();
    trasResolver();
  };

  const verCaptura = (v: VistaTienda) => {
    const t = { chain: v.tienda.chain, num: v.tienda.num };
    const fila = filaDe(llaveFila(t));
    const href = hrefCaptura?.(t) ?? null;
    // A la pestaña Captura sólo si esa tienda sale ahí para esta cuenta; si no,
    // la captura completa de sólo lectura, con la vista que ya se cargó.
    if (href && onIrACaptura && (fila?.verEnCaptura ?? fila?.puedeCapturar)) onIrACaptura(t);
    else setCapturaLectura(v);
  };
  const hrefDe = (t: TiendaSemana | null) =>
    t && (t.verEnCaptura ?? t.puedeCapturar) ? hrefCaptura?.({ chain: t.chain, num: t.num }) ?? null : null;

  const verPropuestas = (t: TiendaRef) => {
    setFiltroPropuestas(t);
    const titulo = document.getElementById(`${ID_SECCION_PROPUESTAS}-titulo`);
    titulo?.focus();
    if (titulo && typeof titulo.scrollIntoView === 'function') titulo.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const alCambiarPropuestas = () => {
    void cargar({ conservarSeleccion: true });
    // Las propuestas cuentan en el número de la pestaña (22-oct-2026): se refresca.
    onCambioRef.current?.();
  };

  // ------------------------------------------------------------ piezas
  const numero = datos?.semana.numero ?? numeroSemana(lunes);

  /**
   * Bajo el nombre de la tienda. En «Seguimiento» (dirección, que ve las dos
   * regionales): el supervisor y el regional, y en otro renglón QUIÉN envió y
   * quién autorizó o rechazó, con cuándo. Si no, el de siempre.
   */
  const subtitulo = (t: TiendaSemana) =>
    soloLectura
      ? [nombrePropio(t.supervisor), t.regional ? `Regional ${nombrePropio(t.regional)}` : ''].filter(Boolean).join(' · ')
      : subtituloTienda(t);
  const lineaSeguimiento = (t: TiendaSemana) => {
    if (!soloLectura) return null;
    const lineas = quienEnvioYResolvio(t);
    return lineas.length ? (
      <span className="max-w-full break-words text-xs text-metas-texto-2" data-testid="seguimiento-tienda">
        {lineas.join(' · ')}
      </span>
    ) : null;
  };

  const insigniaPropuestas = (t: TiendaSemana) => {
    const n = t.propuestasAbiertas ?? 0;
    if (!n) return null;
    return (
      <button
        type="button"
        className="metas-foco mt-1 self-start"
        onClick={(e) => {
          e.stopPropagation();
          verPropuestas({ chain: t.chain, num: t.num });
        }}
        title="Condiciones de tienda o suplentes propuestos: ver la sección de propuestas, más abajo"
        aria-label={`Ver lo de catálogo por resolver de ${t.chain} ${t.num}`}
      >
        <Pill tono="aviso">{n === 1 ? '1 de catálogo por resolver' : `${n} de catálogo por resolver`}</Pill>
      </button>
    );
  };

  const campoBuscar = (
    <Field etiqueta="Buscar" htmlFor="metas-buscar-tienda" className={esMovil ? 'w-full' : 'w-[240px]'}>
      {/* Del alto de los desplegables de al lado (40 px; 44 en el teléfono, como ellos). */}
      <div className={cx(CLASES_CAMPO.normal, 'max-md:h-metas-tactil')} data-testid="caja-buscar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-metas-texto-2">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-4-4" />
        </svg>
        <input
          id="metas-buscar-tienda"
          type="search"
          value={busqueda}
          onChange={(e) => {
            setBusqueda(e.target.value);
            setPagina(0);
            setSeleccion(new Map());
          }}
          placeholder="Tienda o supervisor"
          className={cx('h-full w-full min-w-0 border-0 bg-transparent p-0 outline-none', esMovil && 'text-base')}
        />
      </div>
    </Field>
  );

  const campoRegional = eligeRegional && (
    <Field etiqueta="Regional" htmlFor="metas-regional" className={esMovil ? 'w-full' : 'w-[300px]'}>
      <Dropdown
        inputId="metas-regional"
        value={regional}
        options={[
          { valor: '', etiqueta: `Todos los regionales · ${textoTiendas(tiendas.length)}` },
          ...regionales.map((r) => ({ valor: r.nombre, etiqueta: `${nombrePropio(r.nombre)} · ${textoTiendas(r.tiendas)}` })),
        ]}
        optionLabel="etiqueta"
        optionValue="valor"
        onChange={(e) => {
          const r: string = e.value ?? '';
          setRegional(r);
          // El supervisor elegido que no es de ese regional se suelta: si no, la
          // lista quedaba vacía sin que se viera por qué.
          if (supervisor && r && !tiendas.some((t) => (t.regional || '(sin regional)') === r && supervisorDe(t) === supervisor))
            setSupervisor('');
          setPagina(0);
          setSeleccion(new Map());
        }}
        valueTemplate={valorFiltro}
        panelClassName={CLASE_OVERLAY}
        // 200 px por omisión: en el teléfono se veían 4 opciones (auditoría del 22-oct-2026).
        scrollHeight="320px"
        className="metas-dropdown-filtro w-full"
      />
    </Field>
  );

  // 21-oct-2026 (pedido del regional): sus tiendas por supervisor. Depende del
  // regional elegido —el administrador y dirección escogen primero regional— y,
  // como cualquier filtro, suelta la página y el lote marcado.
  const campoSupervisor = filtraSupervisor && (
    <Field etiqueta="Supervisor" htmlFor="metas-supervisor" className={esMovil ? 'w-full' : 'w-[300px]'}>
      <Dropdown
        inputId="metas-supervisor"
        value={supervisorVigente}
        options={[
          { valor: '', etiqueta: `Todos los supervisores · ${textoTiendas(delRegional.length)}` },
          ...supervisores.map((s) => ({ valor: s.nombre, etiqueta: `${nombrePropio(s.nombre)} · ${textoTiendas(s.tiendas)}` })),
        ]}
        optionLabel="etiqueta"
        optionValue="valor"
        onChange={(e) => {
          setSupervisor(e.value ?? '');
          setPagina(0);
          setSeleccion(new Map());
        }}
        valueTemplate={valorFiltro}
        panelClassName={CLASE_OVERLAY}
        // 200 px por omisión: en el teléfono se veían 4 opciones (auditoría del 22-oct-2026).
        scrollHeight="320px"
        className="metas-dropdown-filtro w-full"
      />
    </Field>
  );

  const chips = (
    <div role="group" aria-label="Filtrar por estado" className={cx('flex gap-2', esMovil ? '-mx-4 overflow-x-auto px-4 pb-1 md:-mx-10 md:px-10' : 'flex-wrap pb-0.5')}>
      {filtrosAutorizacion(capturaTienda).map((f) => (
        <Chip key={f.id} activo={filtro === f.id} onClick={() => cambiarFiltro(f.id)} contador={datos ? conteos[f.id] : null}>
          {etiquetaFiltro(f.id, soloLectura)}
        </Chip>
      ))}
    </div>
  );

  /** «Que venza los martes» (30-sep): en plazo, `autorizadas.xlsx` no se baja, ni para el admin. */
  const bloqueoComisiones = datos?.semana.comisiones?.bloqueo ?? null;
  const botonBajar = alcance.esAdmin && (
    <button
      type="button"
      className={CLASE_BOTON}
      onClick={() => void bajarAutorizadas()}
      disabled={descargando || !!bloqueoComisiones}
      data-testid="bajar-autorizadas"
      aria-describedby={bloqueoComisiones ? 'autorizadas-comisiones-bloqueo' : undefined}
      title={
        bloqueoComisiones ??
        `Sólo las tiendas autorizadas de la semana ${numero}. El archivo se pasa a comisiones-spec-semana.cjs como un archivo de metas más.`
      }
    >
      {descargando ? (
        <i className="pi pi-spin pi-spinner" aria-hidden="true" />
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-metas-exito">
          <path d="M14 3H6v18h12V7z" />
          <path d="M14 3v4h4M9.5 12l5 5M14.5 12l-5 5" />
        </svg>
      )}
      Bajar autorizadas (comisiones)
    </button>
  );

  const errorLista = error && (
    <div role="alert" className="flex flex-wrap items-center gap-2 rounded-metas-control border border-metas-error bg-metas-error-fondo px-4 py-3 text-sm text-metas-error">
      <span className="font-semibold">No se pudo leer la semana: {error}</span>
      <button type="button" className={CLASE_BOTON} onClick={() => void cargar()}>
        Reintentar
      </button>
    </div>
  );

  const nombreDe = (r: TiendaRef) => {
    const t = filaDe(llaveFila(r));
    return t ? nombreTienda(t) : `${r.chain} ${r.num}`;
  };

  const panelResultado = resultado && (
    <div className={cx(CLASE_PANEL, 'flex flex-col gap-2 px-4 py-3 text-sm')} role="status" data-testid="resultado-lote">
      <div className="flex items-start justify-between gap-3">
        <strong className="font-semibold">Resultado de la autorización: {resumenLote(resultado)}</strong>
        <button type="button" className="metas-foco text-[13px] font-semibold text-metas-primario" onClick={() => setResultado(null)} aria-label="Cerrar resultado">
          Cerrar
        </button>
      </div>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {resultado.autorizadas.map((r) => (
          <li key={`a-${llaveFila(r)}`} className="text-metas-exito">
            <i className="pi pi-check mr-2" aria-hidden="true" />
            {nombreDe(r)}: autorizada.
          </li>
        ))}
        {resultado.omitidas.map((r) => (
          <li key={`o-${llaveFila(r)}`} className="text-metas-aviso">
            <i className="pi pi-exclamation-triangle mr-2" aria-hidden="true" />
            {nombreDe(r)}: {r.razon}{' '}
            <button type="button" className="metas-foco font-semibold text-metas-primario underline" onClick={() => abrir(r)}>
              Ver detalle
            </button>
          </li>
        ))}
        {resultado.fallidas.map((r) => (
          <li key={`f-${llaveFila(r)}`} className="text-metas-error">
            <i className="pi pi-times mr-2" aria-hidden="true" />
            {nombreDe(r)}: {r.mensaje}
          </li>
        ))}
      </ul>
    </div>
  );

  const seccionPropuestas = (
    <PropuestasCatalogoSeccion
      onAviso={onAviso}
      activa={activa}
      tiendaFiltro={filtroPropuestas}
      onQuitarFiltro={() => setFiltroPropuestas(null)}
      onCambio={alCambiarPropuestas}
      recarga={recargaPropuestas}
      soloLectura={soloLectura}
    />
  );

  // Lo que dejó la lista vacía, dicho: la búsqueda, o el supervisor o el
  // regional elegidos (antes, un regional sin tiendas en el chip decía «la búsqueda»).
  const textoVacio = () => {
    const chip = `«${etiquetaFiltro(filtro, soloLectura)}»`;
    if (busqueda.trim()) return 'Ninguna tienda coincide con la búsqueda.';
    if (supervisorVigente) return `${nombrePropio(supervisorVigente)} no tiene tiendas en ${chip}.`;
    if (eligeRegional && regional) return `${nombrePropio(regional)} no tiene tiendas en ${chip}.`;
    return `No hay tiendas en ${chip}.`;
  };
  const vacio = datos && !visibles.length && (
    <p className="m-0 px-4 py-6 text-center text-sm text-metas-texto-2" data-testid="lista-vacia">
      {textoVacio()}
    </p>
  );

  const detalle = (
    <PanelDetalleTienda
      lunes={lunes}
      fila={filaElegida}
      alcance={alcance}
      movil={esMovil}
      onResuelto={alResolver}
      onErrorFuera={(m) => onAviso('error', 'No se pudo resolver', m)}
      onRevisionCompletada={() => void cargar({ conservarSeleccion: true })}
      hrefCaptura={hrefDe(filaElegida)}
      onVerCaptura={verCaptura}
      soloLectura={soloLectura}
    />
  );

  const dialogoLectura = <CapturaLecturaDialog vista={capturaLectura} alcance={alcance} onHide={() => setCapturaLectura(null)} />;

  // «Seguimiento» (dirección, diseño `Direccion-seguimiento`, 21-oct-2026): el
  // avance de la semana con los filtros de arriba (regional, supervisor, búsqueda).
  // Sin la captura de la tienda (22-oct-2026), las cifras del flujo de dos pasos.
  const avance = soloLectura && datos ? kpisAvance(base, capturaTienda) : null;
  const bloqueAvance = avance && (
    <section aria-label="Avance de la semana" className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="avance-semana">
      {avance.map((k) => (
        <div key={k.etiqueta} role="group" aria-label={k.etiqueta} className={cx(CLASE_PANEL, 'flex flex-col gap-1 px-4 py-3')}>
          <span className="text-[11px] font-semibold uppercase tracking-[.04em] text-metas-texto-2">{k.etiqueta}</span>
          <span className="num text-2xl font-semibold">
            {k.valor}
            {k.de !== undefined && <span className="text-sm font-normal text-metas-texto-2"> de {k.de}</span>}
          </span>
        </div>
      ))}
    </section>
  );

  // ------------------------------------------------------------ teléfono
  if (esMovil) {
    if (detalleMovil && filaElegida) {
      const i = visibles.findIndex((t) => llaveFila(t) === elegida);
      const anterior = i > 0 ? visibles[i - 1] : null;
      const siguiente = i >= 0 ? visibles[i + 1] ?? null : visibles[0] ?? null;
      return (
        <div className="flex flex-col gap-3" data-testid="autorizacion-tab">
          <header className="-mx-4 flex min-h-14 items-center gap-1 border-b border-metas-borde bg-metas-panel px-2 py-1.5 md:-mx-10 md:px-8">
            <button type="button" className={cx(CLASE_BOTON_ICONO, 'border-transparent bg-transparent !text-metas-texto')} aria-label="Volver a la lista de tiendas" onClick={volverALista}>
              <Flecha dir="atras" />
            </button>
            <div className="flex min-w-0 flex-grow flex-col">
              <span className="truncate text-base font-semibold">{etiquetaFiltro(filtro, soloLectura)}</span>
              {/* Sin `truncate`: si no cabe, parte en dos renglones (la cabecera
                  crece). Cortado, el nombre del regional quedaba en «Regional Jorge Ra…». */}
              <span className="text-xs leading-4 text-metas-texto-2 [overflow-wrap:anywhere]" data-testid="regional-cabecera">
                Sem. {numero} · Regional {nombrePropio(filaElegida.regional) || '—'}
              </span>
            </div>
            <button type="button" className={cx(CLASE_BOTON_ICONO, 'border-transparent bg-transparent')} aria-label="Tienda anterior" disabled={!anterior} onClick={() => anterior && setElegida(llaveFila(anterior))}>
              <Flecha dir="izq" />
            </button>
            <span className="num whitespace-nowrap text-[13px] text-metas-texto-3" data-testid="posicion-tienda" aria-live="polite">
              {i >= 0 ? `${i + 1} de ${visibles.length}` : `— de ${visibles.length}`}
            </span>
            <button type="button" className={cx(CLASE_BOTON_ICONO, 'border-transparent bg-transparent')} aria-label="Tienda siguiente" disabled={!siguiente} onClick={() => siguiente && setElegida(llaveFila(siguiente))}>
              <Flecha dir="der" />
            </button>
          </header>
          {detalle}
          {dialogoLectura}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-4" data-testid="autorizacion-tab">
        {errorLista}
        {bloqueAvance}
        <div className="flex flex-col gap-3">
          {campoRegional}
          {campoSupervisor}
          {campoBuscar}
          {chips}
        </div>
        {panelResultado}
        <ul className="m-0 flex list-none flex-col gap-2 p-0" aria-label="Tiendas" aria-busy={cargando || undefined}>
          {visibles.map((t) => (
            <li key={llaveFila(t)}>
              <button
                type="button"
                className={cx(CLASE_PANEL, 'metas-foco flex w-full flex-col gap-2 p-4 text-left')}
                onClick={() => abrir(t)}
                aria-label={`Ver detalle de ${nombreTienda(t)}`}
                data-tienda-movil={llaveFila(t)}
              >
                <span className="flex w-full items-start justify-between gap-2">
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="font-semibold">{nombreTienda(t)}</span>
                    <span className="text-xs text-metas-texto-2">{subtitulo(t)}</span>
                    {lineaSeguimiento(t)}
                  </span>
                  <EstadoPill estado={t.estado} texto={textoEstadoLista(t.estado, soloLectura, capturaTienda)} />
                </span>
                <MarcaBloqueadaCatalogo t={t} />
                <InsigniasMetaExcepcional t={t} />
                <span className="flex w-full items-center justify-between gap-2 text-[13px]">
                  <span className="num text-metas-texto-2" data-testid="suma-tarjeta">
                    {t.metaTienda.completa ? (
                      <>
                        {pesosEnteros(t.suma ?? null)} de {pesosEnteros(t.metaTienda.monto)}
                        {t.diferenciaPct !== null && t.diferenciaPct !== undefined && (
                          <>
                            {' '}
                            <span className={CLASE_DIF[tonoDiferencia(t.diferenciaPct)]}>{pctConSigno(t.diferenciaPct)}</span>
                          </>
                        )}
                      </>
                    ) : textoMetaIncompleta(t.metaTienda) ? (
                      // Incompleta: lo mismo que su detalle (revisión de la fase B, 21-oct-2026).
                      `${pesosEnteros(t.suma ?? null)} · meta de tienda incompleta (${t.metaTienda.dias} de 7 días)`
                    ) : (
                      // Sin meta de tienda completa no hay contra qué comparar: se decía «$440,155 de — —».
                      `${pesosEnteros(t.suma ?? null)} · sin meta de tienda`
                    )}
                  </span>
                  <CeldaAlertas t={t} />
                </span>
              </button>
            </li>
          ))}
        </ul>
        {!cargando && vacio}
        {cargando && !datos && <p className="m-0 text-sm text-metas-texto-2">Cargando tiendas…</p>}
        {seccionPropuestas}
        {dialogoLectura}
      </div>
    );
  }

  // ------------------------------------------------------------ escritorio
  return (
    <div className="flex flex-col gap-4" data-testid="autorizacion-tab">
      {errorLista}
      {bloqueAvance}
      <div className="flex flex-wrap items-end gap-3">
        {campoRegional}
        {campoSupervisor}
        {campoBuscar}
        <div className="md:ml-2">{chips}</div>
        <div className="flex-grow" />
        {botonBajar}
      </div>

      {alcance.esAdmin && bloqueoComisiones && (
        // El porqué del botón deshabilitado, a la vista (no sólo en `title`).
        <p
          id="autorizadas-comisiones-bloqueo"
          className="m-0 rounded-metas-control border border-metas-banner-borde bg-metas-banner px-4 py-3 text-sm text-metas-banner-texto"
          role="note"
          data-testid="autorizadas-comisiones-bloqueo"
        >
          <strong className="font-semibold">Bajar autorizadas (comisiones):</strong> {bloqueoComisiones}
        </p>
      )}

      {panelResultado}

      {/* El detalle no pasa de 460 px: con 540 (el del artboard, que no tiene la
          barra lateral del panel) a la columna «Tienda» le quedaban 138 px a 1440 y
          el nombre, el supervisor y la hora de envío salían cortados. */}
      <div className="grid items-start gap-6 min-[1200px]:grid-cols-[minmax(0,1fr)_minmax(400px,460px)]">
        <section className={cx(CLASE_PANEL, 'overflow-hidden')} aria-label={soloLectura ? 'Tiendas' : 'Tiendas del regional'}>
          {/* En una sola línea, como el diseño: lo que se acorta es el subtítulo. */}
          <div className="flex items-center gap-3 border-b border-metas-borde-suave px-4 py-3.5">
            <span className="shrink-0 text-[15px] font-semibold">Tiendas</span>
            <span className="min-w-0 truncate text-[13px] text-metas-texto-2" title={`Semana ${numero} · meta de tienda vs. suma capturada`}>
              Semana {numero} · meta de tienda vs. suma capturada
            </span>
            <div className="flex-grow" />
            {soloLectura ? (
              <span className="shrink-0 whitespace-nowrap text-[13px] text-metas-texto-3" data-testid="solo-lectura">
                Sólo lectura
              </span>
            ) : (
              <>
                <span className="shrink-0 whitespace-nowrap text-[13px] text-metas-texto-3" aria-live="polite" data-testid="conteo-seleccion">
                  {marcadas.length === 1 ? '1 seleccionada sin alertas' : `${marcadas.length} seleccionadas sin alertas`}
                </span>
                <button
                  type="button"
                  // Con `!`: CLASE_BOTON_PRIMARIO ya trae `px-4` y `text-sm`, que ganaban.
                  className={cx(CLASE_BOTON_PRIMARIO, '!h-9 !px-3.5 !text-[13px]')}
                  onClick={autorizarSeleccion}
                  disabled={!marcadas.length || trabajando}
                >
                  {trabajando ? (
                    <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12l5 5 9-10" />
                    </svg>
                  )}
                  Autorizar seleccionadas
                </button>
              </>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] table-fixed border-collapse" aria-busy={cargando || undefined}>
              <colgroup>
                {!soloLectura && <col className="w-12" />}
                <col />
                <col className="w-[124px]" />
                <col className="w-[108px]" />
                <col className="w-[120px]" />
                <col className="w-[84px]" />
                <col className="w-[76px]" />
              </colgroup>
              <thead className="bg-metas-cabecera">
                <tr>
                  {!soloLectura && (
                    <th scope="col" className="px-3 py-3 text-center">
                      <input
                        ref={casillaTodas}
                        type="checkbox"
                        className="h-[18px] w-[18px] accent-metas-primario"
                        aria-label="Seleccionar las enviadas de esta página"
                        title="Sólo las de esta página que no tienen alertas"
                        checked={todasMarcadas}
                        disabled={!seleccionables.length}
                        onChange={(e) => marcarTodas(e.target.checked)}
                      />
                    </th>
                  )}
                  <th scope="col" className="px-3 py-3 text-left text-metas-th text-metas-texto-2">Tienda</th>
                  <th scope="col" className="px-3 py-3 text-left text-metas-th text-metas-texto-2">Estado</th>
                  <th scope="col" className="px-3 py-3 text-right text-metas-th text-metas-texto-2">Meta tienda</th>
                  <th scope="col" className="px-3 py-3 text-right text-metas-th text-metas-texto-2">Suma capturada</th>
                  <th scope="col" className="px-3 py-3 text-right text-metas-th text-metas-texto-2">Dif.</th>
                  <th scope="col" className="px-3 py-3 text-center text-metas-th text-metas-texto-2">Alertas</th>
                </tr>
              </thead>
              <tbody>
                {pag.filas.map((t) => {
                  const k = llaveFila(t);
                  const sel = k === elegida;
                  const enviadaMia = t.estado === 'enviada' && t.puedeAutorizar;
                  return (
                    <tr
                      key={k}
                      className={cx('cursor-pointer border-t border-metas-borde-suave hover:bg-metas-cabecera', sel && '!bg-metas-fila-sel')}
                      aria-selected={sel}
                      onClick={() => abrir(t)}
                      data-tienda={k}
                    >
                      {!soloLectura && (
                        <td className={cx('px-3 py-3 text-center', sel && 'shadow-metas-marca-sel')} onClick={(e) => e.stopPropagation()}>
                          {enviadaMia && (
                            <input
                              type="checkbox"
                              // 24 px en pantalla táctil (medía 18; auditoría del 22-oct-2026).
                              className="h-[18px] w-[18px] accent-metas-primario disabled:cursor-not-allowed [@media(pointer:coarse)]:h-6 [@media(pointer:coarse)]:w-6"
                              aria-label={
                                t.puedeAutorizarEnLote
                                  ? `Seleccionar ${nombreTienda(t)}`
                                  : bloqueadaPorCatalogo(t) || (t.fueraDeLote && t.alertas === 0)
                                    ? `${nombreTienda(t)}: ${porQueFueraDeLote(t)}`
                                    : `${nombreTienda(t)}: con alertas o sin revisar, se autoriza desde el detalle`
                              }
                              title={t.puedeAutorizarEnLote ? undefined : porQueFueraDeLote(t)}
                              checked={seleccion.has(k)}
                              disabled={!t.puedeAutorizarEnLote}
                              onChange={(e) => marcar(t, e.target.checked)}
                            />
                          )}
                        </td>
                      )}
                      {/* La elegida, además del fondo, con la barra azul a la izquierda (en la primera celda). */}
                      <td className={cx('px-3 py-3', sel && soloLectura && 'shadow-metas-marca-sel')}>
                        <div className="flex min-w-0 flex-col items-start gap-0.5">
                          <button
                            type="button"
                            // Parte de renglón antes que cortarse: «VAZ 1…» no distinguía VAZ 10, 18, 139 o 198.
                            className="metas-foco max-w-full text-left text-sm font-semibold text-metas-texto [overflow-wrap:anywhere]"
                            onClick={(e) => {
                              e.stopPropagation();
                              abrir(t);
                            }}
                            aria-label={`Ver detalle de ${nombreTienda(t)}`}
                            aria-pressed={sel}
                            title={nombreTienda(t)}
                          >
                            {nombreTienda(t)}
                          </button>
                          {/* Se parte en dos líneas antes que cortarse: la hora de envío es lo último y lo que se perdía. */}
                          <span className="max-w-full break-words text-xs text-metas-texto-2" data-testid="subtitulo-tienda">
                            {subtitulo(t)}
                          </span>
                          {lineaSeguimiento(t)}
                          <MarcaBloqueadaCatalogo t={t} />
                          <InsigniasMetaExcepcional t={t} />
                          {insigniaPropuestas(t)}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <EstadoPill estado={t.estado} texto={textoEstadoLista(t.estado, soloLectura, capturaTienda)} />
                      </td>
                      <td className="num px-3 py-3 text-right text-sm" data-testid={`meta-tienda-${k}`} title={textoMetaIncompleta(t.metaTienda) ?? undefined}>
                        {t.metaTienda.completa ? (
                          pesosEnteros(t.metaTienda.monto)
                        ) : textoMetaIncompleta(t.metaTienda) ? (
                          // Incompleta: el monto de los días que hay y cuántos son, como el detalle
                          // y el recuadro de la Captura (antes «—», y el detalle decía otra cosa).
                          <span className="flex flex-col items-end text-metas-aviso-oscuro">
                            {pesosEnteros(t.metaTienda.monto)}
                            <span className="text-xs font-normal">{t.metaTienda.dias} de 7 días</span>
                          </span>
                        ) : (
                          <span className="text-metas-texto-2" title="Sin meta de tienda completa (7 días, sin IVA)">—</span>
                        )}
                      </td>
                      <td className="num px-3 py-3 text-right text-sm">{pesosEnteros(t.suma ?? null)}</td>
                      <td className="num px-3 py-3 text-right text-sm">
                        <span className={CLASE_DIF[tonoDiferencia(t.diferenciaPct)]}>{pctConSigno(t.diferenciaPct ?? null)}</span>
                      </td>
                      <td className="px-3 py-3 text-center text-sm">
                        <CeldaAlertas t={t} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {cargando && !datos && <p className="m-0 px-4 py-6 text-sm text-metas-texto-2">Cargando tiendas…</p>}
          {!cargando && vacio}
          <div className="flex items-center justify-between border-t border-metas-borde-suave px-4 py-3 text-[13px] text-metas-texto-2">
            <span data-testid="paginacion">
              Mostrando {pag.filas.length} de {visibles.length} {visibles.length === 1 ? 'tienda' : 'tiendas'}
            </span>
            <div className="flex gap-2">
              <button type="button" className={cx(CLASE_BOTON_ICONO, '!h-8 !w-8')} aria-label="Página anterior" disabled={pag.pagina === 0} onClick={() => setPagina(pag.pagina - 1)}>
                <Flecha dir="izq" />
              </button>
              <button type="button" className={cx(CLASE_BOTON_ICONO, '!h-8 !w-8')} aria-label="Página siguiente" disabled={pag.pagina >= pag.paginas - 1} onClick={() => setPagina(pag.pagina + 1)}>
                <Flecha dir="der" />
              </button>
            </div>
          </div>
        </section>

        <div ref={refDetalle} className="min-w-0 scroll-mt-4" data-testid="contenedor-detalle">
          {detalle}
        </div>
      </div>

      {seccionPropuestas}
      {dialogoLectura}
    </div>
  );
};

export default AutorizacionTab;
